import { isRecord, jsonText } from '../../json';
import { readStoredJson, writeStoredJson } from '../../storage';
import { HOUSE_BRIDGE, TRANSPORT_PREFERENCE, transportPreferenceFrom } from '../catalog/link';
import type { TransportPreference } from '../catalog/link';
import { STORAGE_KEY } from '../catalog/storage';

/**
 * Link settings, local to this tablet like the rest of the configuration.
 *
 * There is no broker address here, and no broker credentials: the tablet never
 * talks to the broker. It talks to the gateway, which holds that connection, and
 * the gateway holds the broker's secrets. One credential instead of two, and one
 * place to change when it rotates.
 *
 * The password is stored the way the technician PIN is: in localStorage, which
 * is obfuscation at best. It is never written into an event, a payload preview,
 * a description or a log line — see `redact` and `describeDocument`.
 */
export interface LinkSettings {
  /**
   * Where the gateway is. Empty means wherever this app was served from, which
   * is the arrangement the deployment expects: one host serving both.
   */
  readonly bridgeUrl: string;
  readonly bridgeUsername: string;
  readonly bridgePassword: string;
  /**
   * Which channel commands leave on. Stored with the rest because it is the same
   * decision: how this tablet talks to this RCU.
   */
  readonly transport: TransportPreference;
}

export const emptyLinkSettings = (): LinkSettings => ({
  bridgeUrl: HOUSE_BRIDGE.URL,
  bridgeUsername: HOUSE_BRIDGE.USERNAME,
  bridgePassword: HOUSE_BRIDGE.PASSWORD,
  transport: TRANSPORT_PREFERENCE.AUTOMATIC,
});

const decodeLinkSettings = (value: unknown): LinkSettings | null => {
  if (!isRecord(value)) return null;
  return {
    bridgeUrl: jsonText(value.bridgeUrl),
    bridgeUsername: jsonText(value.bridgeUsername),
    bridgePassword: jsonText(value.bridgePassword),
    transport: transportPreferenceFrom(value.transport),
  };
};

/** What a field falls back to: what the technician saved, or the house gateway. */
const houseDefault = (stored: string, house: string): string =>
  stored.trim() === '' ? house : stored;

/** Reads what was saved; an unreadable entry is replaced rather than half-believed. */
export const loadLinkSettings = (): LinkSettings => {
  const house = emptyLinkSettings();
  const stored = decodeLinkSettings(readStoredJson(STORAGE_KEY.LINK));
  if (stored === null) return house;
  return {
    ...stored,
    bridgeUrl: houseDefault(stored.bridgeUrl, house.bridgeUrl),
    bridgeUsername: houseDefault(stored.bridgeUsername, house.bridgeUsername),
    bridgePassword: houseDefault(stored.bridgePassword, house.bridgePassword),
  };
};

export const saveLinkSettings = (settings: LinkSettings): void => {
  writeStoredJson(STORAGE_KEY.LINK, settings);
};

/** Replaces a stored password with a fixed marker, for anything on screen. */
export const REDACTED = '••••••••';

export const redact = (settings: LinkSettings): LinkSettings => ({
  ...settings,
  bridgePassword: settings.bridgePassword === '' ? '' : REDACTED,
});
